import { Checkbox } from '@heroui/react';
import { ColumnDef } from '@tanstack/react-table';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { useGetAllUsers, UserWithHotel } from '../../queries';
import { UsersColumns as BaseUsersColumns } from '../../views/Users/pages/Users/columns';
import { DataTable } from '..';

interface UserSelectorProps {
  onUserSelectionChange?: (attraction: string[]) => void;
  defaultSelectedKey?: string[];
}

type SelectCellProps = {
  id: string;
  selected: boolean;
  onToggle: (id: string, checked: boolean) => void;
};

const UserSelectCell: React.FC<SelectCellProps> = ({ id, selected, onToggle }) => (
  <Checkbox
    isSelected={selected}
    onValueChange={(checked) => onToggle(id, checked)}
  />
);

const PAGE_SIZE = 20;

export const UserSelector: FC<UserSelectorProps> = ({
  onUserSelectionChange,
  defaultSelectedKey,
}) => {
  const [selectedKey, setSelectedKey] = useState<string[]>([]);

  useEffect(() => {
    if (defaultSelectedKey) {
      setSelectedKey(defaultSelectedKey);
    }
  }, [defaultSelectedKey]);

  useEffect(() => {
    if (onUserSelectionChange) {
      onUserSelectionChange(selectedKey);
    }
  }, [selectedKey, onUserSelectionChange]);

  const { data: users, isLoading, isError, error } = useGetAllUsers();

  // Każde konto zakładane w panelu ma dziś `staffService` — filtr po tej fladze
  // wyciąłby wszystkich odbiorców, więc InMaile kierujemy do pełnej listy.
  const visibleUsers = useMemo(() => users ?? [], [users]);

  const toggleSelection = (id: string, checked: boolean) => {
    setSelectedKey((prev) => (checked
      ? Array.from(new Set([...prev, id]))
      : prev.filter((item) => item !== id)));
  };

  const allUserIds = useMemo(
    () => visibleUsers.map((item) => item.id),
    [visibleUsers],
  );

  const allSelected = allUserIds.length > 0
    && allUserIds.every((id) => selectedKey.includes(id));

  const selectorColumns: ColumnDef<UserWithHotel>[] = useMemo(
    () => [
      {
        id: 'select',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => (
          <Checkbox
            isSelected={allSelected}
            onValueChange={(checked) => {
              if (checked) {
                setSelectedKey(allUserIds);
              } else {
                setSelectedKey([]);
              }
            }}
          />
        ),
        enableSorting: false,
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <UserSelectCell
            id={row.original.id}
            selected={selectedKey.includes(row.original.id)}
            onToggle={toggleSelection}
          />
        ),
      },
      /*
       * Trzy kolumny, nie cała tabela użytkowników. Wybór odbiorcy sprowadza się do
       * pytania „kto to i z którego hotelu"; telefon, uprawnienia, data założenia konta
       * i ostatnie logowanie tylko rozpychały tabelę, która stoi teraz w szufladzie na
       * pół ekranu.
       */
      ...BaseUsersColumns
        .filter((col) => {
          const { accessorKey } = (col as { accessorKey?: string });
          return accessorKey === 'name' || accessorKey === 'email' || accessorKey === 'organizations';
        })
        .map((col) => {
          const { accessorKey } = (col as { accessorKey?: string });
          if (accessorKey !== 'organizations') return col as ColumnDef<UserWithHotel>;
          // Ta sama zawartość, czytelniejszy nagłówek: w tym kontekście to po prostu hotel.
          return { ...col, header: 'Hotel' } as ColumnDef<UserWithHotel>;
        }),
    ],
    [allSelected, allUserIds, selectedKey],
  );

  return (
    <div>
      {isLoading && <div>Loading...</div>}
      {isError && !isLoading && (
        <div>
          Error:
          {' '}
          {error?.message}
        </div>
      )}
      {!isLoading && !isError && (
        <DataTable
          data={visibleUsers}
          columns={selectorColumns}
          isLoading={isLoading}
          pageSize={PAGE_SIZE}
          searchPlaceholder="Search users..."
        />
      )}
    </div>
  );
};
